import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ThumbsUp, ThumbsDown, Copy, MessageCircle } from 'lucide-react';
import { getOrderDetail, rateOrder, followUser } from '../../services/p2pService';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const FEEDBACK_TAGS = ['Fast transaction', 'Polite', 'Patient', 'Safe', 'Good price'];

function FeedbackSheet({ order, isBuyer, onClose, onDone }: { order: any; isBuyer: boolean; onClose: () => void; onDone: () => void }) {
    const { toast } = useToast();
    const [positive, setPositive] = useState(true);
    const [tags, setTags] = useState<string[]>([]);
    const [comment, setComment] = useState('');
    const [anon, setAnon] = useState(false);
    const [loading, setLoading] = useState(false);

    const alreadyRated = isBuyer ? !!order.rating?.byBuyer?.score : !!order.rating?.bySeller?.score;

    const submit = async () => {
        setLoading(true);
        try {
            await rateOrder(order._id, { score: positive ? 5 : 2, comment, tags, anonymous: anon });
            toast('Feedback submitted!', 'success');
            onDone();
        } catch (e: any) { toast(e.message, 'error'); }
        setLoading(false);
    };

    if (alreadyRated) return (
        <>
            <div className="p2p-overlay" onClick={onClose} />
            <div className="p2p-bottom-sheet">
                <div className="p2p-sheet-handle" />
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#555' }}>You have already rated this trade.</div>
            </div>
        </>
    );

    return (
        <>
            <div className="p2p-overlay" onClick={onClose} />
            <div className="p2p-bottom-sheet">
                <div className="p2p-sheet-handle" />
                <div className="p2p-sheet-title">Rate Your P2P Experience</div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                    <button
                        onClick={() => setPositive(true)}
                        style={{ flex: 1, height: 48, borderRadius: 8, border: '2px solid', borderColor: positive ? '#1CD4A7' : '#ddd', background: positive ? 'rgba(28,212,167,0.1)' : '#f4f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: positive ? '#1CD4A7' : '#555', fontWeight: 600 }}
                    >
                        <ThumbsUp size={16} />Positive
                    </button>
                    <button
                        onClick={() => setPositive(false)}
                        style={{ flex: 1, height: 48, borderRadius: 8, border: '2px solid', borderColor: !positive ? '#F6465D' : '#ddd', background: !positive ? 'rgba(246,70,93,0.1)' : '#f4f4f4', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: !positive ? '#F6465D' : '#555', fontWeight: 600 }}
                    >
                        <ThumbsDown size={16} />Negative
                    </button>
                </div>
                <div className="p2p-chip-row" style={{ marginBottom: 16 }}>
                    {FEEDBACK_TAGS.map(t => (
                        <button
                            key={t}
                            className={`p2p-chip ${tags.includes(t) ? 'selected' : ''}`}
                            style={{ color: tags.includes(t) ? '#1CD4A7' : '#555' }}
                            onClick={() => setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                        >
                            {t}
                        </button>
                    ))}
                </div>
                <textarea
                    className="p2p-input"
                    style={{ color: '#000', background: '#f4f4f4', borderColor: '#ddd', minHeight: 80, resize: 'none', marginBottom: 12 }}
                    placeholder="Leave a comment (optional, max 500 chars)..."
                    maxLength={500}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                />
                <label className="p2p-checkbox-row" style={{ marginBottom: 16 }}>
                    <input type="checkbox" className="p2p-checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} />
                    <span style={{ fontSize: 13, color: '#555' }}>Submit anonymously</span>
                </label>
                <button className="p2p-btn-yellow" style={{ color: '#000' }} disabled={loading} onClick={submit}>
                    {loading ? 'Submitting...' : 'Leave Comments'}
                </button>
            </div>
        </>
    );
}

function CopyBtn({ text }: { text: string }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button onClick={copy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#1CD4A7' : 'var(--p2p-text-muted)', padding: '2px 4px', display: 'flex', alignItems: 'center' }}>
            <Copy size={13} />
        </button>
    );
}

export default function OrderDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { toast } = useToast();
    const [order, setOrder] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showFeedback, setShowFeedback] = useState(false);
    const [following, setFollowing] = useState(false);

    useEffect(() => {
        getOrderDetail(id!).then(o => { setOrder(o); }).catch(() => {}).finally(() => setLoading(false));
    }, [id]);

    if (loading) return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span className="p2p-skel" style={{ width: '60%', height: 20 }} />
            <span className="p2p-skel" style={{ width: '100%', height: 14 }} />
            <span className="p2p-skel" style={{ width: '80%', height: 14 }} />
            <span className="p2p-skel" style={{ width: '100%', height: 80, borderRadius: 8 }} />
        </div>
    );
    if (!order) return <div className="p2p-empty"><p className="p2p-empty-text">Order not found</p></div>;

    const isBuyer = order.buyer?._id === user?._id;
    const counterparty = isBuyer ? order.seller : order.buyer;
    const pm = order.paymentMethod;

    // fee breakdown
    const takerFee = order.takerFee ?? 0;
    const makerFee = order.makerFee ?? 0;
    const totalFee = isBuyer ? takerFee : makerFee;
    const grossAmount = order.cryptoAmount ?? 0;
    const netAmount = isBuyer ? grossAmount - totalFee : grossAmount;

    const createdAt = order.createdAt ? new Date(order.createdAt).toLocaleString('sv').replace('T', ' ') : '—';

    const handleFollow = async () => {
        try {
            const res = await followUser(counterparty._id);
            setFollowing(res.following);
            toast(res.following ? 'Following' : 'Unfollowed', 'success');
        } catch (e: any) { toast(e.message, 'error'); }
    };

    const sideColor = isBuyer ? 'var(--p2p-buy)' : 'var(--p2p-sell)';
    const sideLabel = isBuyer ? 'Buy' : 'Sell';

    return (
        <div className="p2p-shell">
            <div className="p2p-page">
            {/* Header */}
            <div className="p2p-header">
                <button className="p2p-back-btn" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} />Order Details
                </button>
            </div>

            <div className="p2p-page-content" style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' }}>

                {/* ── Hero: Amount + Status ── */}
                <div style={{ textAlign: 'center', padding: '28px 16px 20px' }}>
                    <div style={{ fontSize: 34, fontWeight: 700, color: isBuyer ? 'var(--p2p-buy)' : 'var(--p2p-sell)', fontVariantNumeric: 'tabular-nums', marginBottom: 8 }}>
                        {isBuyer ? '+' : '-'}{netAmount.toFixed(2)} {order.crypto}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 8 }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--p2p-buy)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={11} color="#000" strokeWidth={3} />
                        </div>
                        <span style={{ fontSize: 14, color: 'var(--p2p-buy)', fontWeight: 600 }}>Order Completed</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>
                        {netAmount.toFixed(2)} {order.crypto} deposit into your Spot account
                    </div>
                </div>

                {/* ── Main Info Card ── */}
                <div style={{ margin: '0 16px 12px', background: 'var(--p2p-bg-secondary)', borderRadius: 12, border: '1px solid var(--p2p-border-default)', overflow: 'hidden' }}>

                    {/* Card header: side + Chat button */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 16, fontWeight: 700, color: sideColor }}>{sideLabel}</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--p2p-text-primary)' }}>{order.crypto}</span>
                        </div>
                        <button
                            onClick={() => navigate(`/p2p/order/${order._id}`)}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#F0B90B', color: '#000', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                        >
                            <MessageCircle size={14} />Chat
                        </button>
                    </div>

                    {/* Fiat Amount */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                        <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>Fiat Amount</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--p2p-text-primary)' }}>
                            {order.fiat === 'KES' ? 'KSh ' : ''}{order.fiatAmount?.toLocaleString()} {order.fiat !== 'KES' ? order.fiat : ''}
                        </span>
                    </div>

                    {/* Price */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                        <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>Price</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--p2p-text-primary)' }}>
                            {order.fiat === 'KES' ? 'KSh ' : ''}{order.price?.toLocaleString()} {order.fiat !== 'KES' ? order.fiat : ''}
                        </span>
                    </div>

                    {/* Receive Quantity with fee breakdown */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: totalFee > 0 ? 8 : 0 }}>
                            <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>Receive Quantity</span>
                            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--p2p-text-primary)' }}>{netAmount.toFixed(2)} {order.crypto}</span>
                        </div>
                        {totalFee > 0 && (
                            <div style={{ borderLeft: '2px solid var(--p2p-border-default)', paddingLeft: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>Total Quantity</span>
                                    <span style={{ fontSize: 12, color: 'var(--p2p-text-secondary)' }}>{grossAmount.toFixed(2)} {order.crypto}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: 12, color: 'var(--p2p-text-muted)' }}>Fee</span>
                                    <span style={{ fontSize: 12, color: 'var(--p2p-sell)' }}>-{totalFee.toFixed(4)} {order.crypto}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Order No. */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                        <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>Order No.</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontSize: 13, color: 'var(--p2p-text-secondary)', fontFamily: 'monospace' }}>{order.orderNumber ?? order._id}</span>
                            <CopyBtn text={order.orderNumber ?? order._id} />
                        </div>
                    </div>

                    {/* Created Time */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--p2p-border-default)' }}>
                        <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>Created Time</span>
                        <span style={{ fontSize: 13, color: 'var(--p2p-text-secondary)' }}>{createdAt}</span>
                    </div>

                    {/* Counterparty nickname */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                        <span style={{ fontSize: 13, color: 'var(--p2p-text-muted)' }}>{isBuyer ? "Seller's Nickname" : "Buyer's Nickname"}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 13, color: 'var(--p2p-text-secondary)', fontWeight: 500 }}>{counterparty?.nickname ?? counterparty?.name ?? '—'}</span>
                            <div className="p2p-avatar" style={{ width: 20, height: 20, fontSize: 10 }}>
                                {(counterparty?.nickname ?? counterparty?.name ?? '?').slice(0, 1).toUpperCase()}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Payment Method ── */}
                {pm && (
                    <div style={{ margin: '0 16px 12px', background: 'var(--p2p-bg-secondary)', borderRadius: 12, border: '1px solid var(--p2p-border-default)', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--p2p-text-primary)' }}>Payment method</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: pm.methodColor ?? '#2563eb', flexShrink: 0, display: 'inline-block' }} />
                            <span style={{ fontSize: 13, color: 'var(--p2p-text-secondary)', fontWeight: 500 }}>{pm.methodType}</span>
                        </div>
                    </div>
                )}

                {/* ── Need Help ── */}
                <div
                    style={{ margin: '0 16px 12px', background: 'var(--p2p-bg-secondary)', borderRadius: 12, border: '1px solid var(--p2p-border-default)', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => navigate('/dashboard/support')}
                >
                    <span style={{ fontSize: 14, color: 'var(--p2p-text-primary)' }}>Need help</span>
                    <span style={{ fontSize: 18, color: 'var(--p2p-text-muted)' }}>›</span>
                </div>

                {/* ── Leave Feedback + Follow ── */}
                <div style={{ margin: '0 16px 12px', background: 'var(--p2p-bg-secondary)', borderRadius: 12, border: '1px solid var(--p2p-border-default)', overflow: 'hidden' }}>
                    <div
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--p2p-border-default)', cursor: 'pointer' }}
                        onClick={() => setShowFeedback(true)}
                    >
                        <span style={{ fontSize: 14, color: 'var(--p2p-text-primary)' }}>Leave Feedback</span>
                        <span style={{ fontSize: 18, color: 'var(--p2p-text-muted)' }}>›</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px' }}>
                        <span style={{ fontSize: 14, color: 'var(--p2p-text-primary)' }}>Follow this user</span>
                        <button className={`p2p-toggle ${following ? 'on' : ''}`} onClick={handleFollow} />
                    </div>
                </div>
            </div>

            {/* ── Bottom CTA ── */}
            <div style={{ position: 'sticky', bottom: 0, padding: '12px 16px', background: 'var(--p2p-bg-primary)', borderTop: '1px solid var(--p2p-border-default)' }}>
                <button
                    className="p2p-btn-yellow"
                    onClick={() => navigate('/p2p')}
                >
                    Order again
                </button>
            </div>

            {showFeedback && (
                <FeedbackSheet
                    order={order}
                    isBuyer={isBuyer}
                    onClose={() => setShowFeedback(false)}
                    onDone={() => { setShowFeedback(false); getOrderDetail(id!).then(setOrder).catch(() => {}); }}
                />
            )}
            </div>
        </div>
    );
}
